import { useMutation } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { useToast } from '@pocketlaw/tetris';
import { GetEsigningRecipientsDocument } from 'app/domains/esigning/hooks/useEsigningRecipients/query.gql';
import { useLastSignRequest } from 'app/domains/esigning/hooks/useLastSignRequest';
import { useLeaveDocumentPrompt } from 'app/domains/esigning/hooks/useLeaveDocumentPrompt';
import { SignRequestStatus } from 'shared/domains/apollo/generated/types';
import { documentFlush, usePreview } from 'shared/domains/documents';
import { CancelSignRequestDocument } from './mutation.gql';
import { GetDocumentAttachmentKindsDocument } from '../../../queries';
export const useCancelSignRequest = (documentId) => {
    const { lastSignRequest } = useLastSignRequest();
    const { id = '', status } = lastSignRequest !== null && lastSignRequest !== void 0 ? lastSignRequest : {};
    const { addToast } = useToast();
    const { isRtcAvailable } = usePreview();
    const handleError = () => {
        if (isRtcAvailable) {
            documentFlush({ state: 'not-initialized' });
        }
        addToast({
            appearance: 'danger',
            title: t({
                message: 'Could not cancel e-signing request.',
                comment: 'Message for when a user tried to cancel an e-signing request but something went wrong',
            }),
        });
    };
    const [mutate, { error, loading }] = useMutation(CancelSignRequestDocument, {
        variables: {
            signRequestId: id,
        },
        onError() {
            handleError();
        },
        onCompleted(data) {
            var _a;
            // eslint-disable-next-line no-underscore-dangle
            if (((_a = data.cancelSignRequest) === null || _a === void 0 ? void 0 : _a.__typename) === 'CancelSignRequestRejectionList') {
                handleError();
            }
        },
    });
    useLeaveDocumentPrompt({
        busy: loading,
        message: t({
            message: 'If you leave now your document might not be cancelled',
            comment: 'Message for when the users leaves a document sent for e-signing while it is being cancelled',
        }),
    });
    const cancelSignRequest = () => {
        if (isRtcAvailable) {
            documentFlush({ state: 'initialized' });
        }
        return mutate({
            refetchQueries: [
                { query: GetDocumentAttachmentKindsDocument, variables: { documentId } },
                { query: GetEsigningRecipientsDocument, variables: { documentId } },
            ],
        });
    };
    return {
        cancelSignRequest,
        loading,
        error: Boolean(error || status === SignRequestStatus.Failed),
    };
};
